import React, { useState } from 'react';
import ProfileAvatar from '../common/ProfileAvatar';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Button } from '../common/Button';
import FormattedCurrency from '../common/FormattedCurrency';
import EyeButton from '../common/EyeButton';

import type { Quote } from '../../types/quote';
import { InputGroup } from '../common/InputGroup';
import { DropdownSelect } from '../common/DropdownSelect';
import { UserAlliance } from '../../types/alliance';
import { QuoteAlliance } from '../../types/quote';
import { useQuoteStore } from '../../stores/quoteStore';
import { ServiceCategory } from '../../types/service';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { computeQuoteAmount } from '../../utils/jobUtils';
import QuoteAllianceInviteModal from './QuoteAllianceInviteModal';
import FullBlockingModal from '../common/FullBlockingModal';

interface FreelancerDetails {
  id: string;
  first_name: string;
  last_name: string | null;
  avatar_url?: string | null;
  service_category_slugs?: string[];
  service_categories?: ServiceCategory[];
  full_access?: boolean;
}

interface QuoteFreelancerCardProps {
  freelancer: FreelancerDetails;
  onClick?: () => void;
  className?: string;

  userAlliance?: UserAlliance;
  quoteAlliance?: QuoteAlliance;
  quote?: Quote;

  editMode?: boolean;
}

const QuoteFreelancerCard: React.FC<QuoteFreelancerCardProps> = ({
  freelancer,
  className,
  editMode = false,
  userAlliance,
  quoteAlliance,
  quote,
  onClick,
}) => {
  const { t } = useTranslation(['quotes', 'common', 'alliances']);
  const { retireQuoteAlliance, updateQuoteAllianceServiceCategory } = useQuoteStore();

  const computedOffer = (() => {
    if (quoteAlliance) {
      return quoteAlliance.child_quote ? computeQuoteAmount(quoteAlliance.child_quote) : 0;
    }

    if (userAlliance) {
      return null;
    }

    // If we have a quote (for the main quote owner), compute from the quote itself
    if (quote) {
      return computeQuoteAmount(quote);
    }

    return null;
  })();

  const {
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
  } = useServiceCategorySelector(
    quoteAlliance?.service_category ? [quoteAlliance.service_category] : []
  );

  const selectedServiceCategory = selectedServiceCategories[0];
  const selectedServiceCategoryId = selectedServiceCategory?.id;

  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Alliance status

  const isQuoteAlliancePending = quoteAlliance?.status === 'pending';
  const isQuoteAllianceAccepted = quoteAlliance?.status === 'accepted';

  // Button visibility logic - only show buttons if we have alliance data
  const showSendRequestButton = editMode && !isQuoteAlliancePending && !isQuoteAllianceAccepted;
  const showCancelInviteButton = editMode && isQuoteAlliancePending;
  const showRemoveSubmissionButton = editMode && isQuoteAllianceAccepted;
  const [showInviteModal, setShowInviteModal] = useState(false);

  const handleServiceCategoryChange = async (value: string, item?: ServiceCategory) => {
    if (item) {
      setSelectedServiceCategories([item]);
      setError(null);
      if (quoteAlliance) {
        try {
          await updateQuoteAllianceServiceCategory(quoteAlliance.id, item.id);
        } catch (error) {
          console.error('Error updating service category:', error);
          setError(t('createQuoteModal.unknownErrorUpdatingServiceCategory'));
        }
      }
    } else {
      console.error(
        `No service category selected. This is not supposed to happen. Value: ${value}`
      );
    }
  };

  const handleInviteAlliance = async () => {
    if (!userAlliance || !quote || !selectedServiceCategoryId) return;
    setShowInviteModal(true);
  };

  const handleRetireAlliance = async () => {
    if (!quoteAlliance) return;

    setIsBusy(true);
    setError(null);
    try {
      await retireQuoteAlliance(quoteAlliance.id);
    } catch (e) {
      console.error(e);
      const errorMessage = showCancelInviteButton
        ? t('createQuoteModal.unknownCancelAllianceRequestError')
        : t('createQuoteModal.unknownRetireAllianceError');
      setError(errorMessage);
    } finally {
      setIsBusy(false);
      setShowRemoveConfirm(false);
    }
  };

  // Render offer section
  const renderOffer = () => {
    if (computedOffer === null || computedOffer === undefined) return null;

    return (
      <div className="flex flex-col flex-shrink-0">
        <span className="text-sm leading-tight text-secondary">
          {isQuoteAlliancePending
            ? t('createQuoteModal.quoteRequestLabel')
            : t('createQuoteModal.offerLabel')}
        </span>
        {computedOffer === 0 ? (
          <span className="text-base font-bold leading-tight text-secondary">
            {t('childQuoteOfferPending')}
          </span>
        ) : (
          <FormattedCurrency value={computedOffer} />
        )}
      </div>
    );
  };

  const renderServiceCategoryDropdown = () => {
    if (!freelancer.service_categories || !editMode) return null;

    return (
      <div className="w-full md:min-w-[19rem]">
        <InputGroup disabled={isQuoteAlliancePending || isQuoteAllianceAccepted}>
          <div className="relative">
            <DropdownSelect<ServiceCategory>
              value={selectedServiceCategories[0]}
              onChange={handleServiceCategoryChange}
              options={freelancer.service_categories}
              getOptionLabel={item => item.label}
              getOptionValue={item => item.id}
              placeholder={t('chooseRole', {
                ns: 'alliances',
                defaultValue: "Sélectionnez le rôle de l'Alliance",
              })}
              className="mt-1"
            />
            {editMode &&
              selectedServiceCategory &&
              !isQuoteAlliancePending &&
              !isQuoteAllianceAccepted && (
                <button
                  onClick={() => {
                    setSelectedServiceCategories([]);
                    setError(null);
                  }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-secondary p-1"
                  aria-label={t('common:form_actions.cancel')}
                  title={t('common:form_actions.cancel')}
                >
                  &times;
                </button>
              )}
          </div>
        </InputGroup>
      </div>
    );
  };

  return (
    <div className={twMerge(`flex flex-col gap-4 py-3 md:flex md:flex-row md:gap-16`, className)}>
      <div className="flex flex-row gap-4">
        <ProfileAvatar
          profile_id={freelancer.id}
          first_name={freelancer.first_name}
          avatar_url={freelancer.avatar_url}
          className="flex-shrink-0"
        />

        <div>
          <div className="font-bold leading-tight">
            {freelancer.first_name} {freelancer.last_name}
          </div>
          {selectedServiceCategory && !editMode && (
            <div className="text-sm leading-tight">{selectedServiceCategory.label}</div>
          )}
        </div>

        {onClick && (
          <EyeButton
            onClick={onClick}
            ariaLabel={t('createQuoteModal.showAllianceButtonLabel')}
            outerClassName="ml-auto md:hidden"
          />
        )}
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:gap-16">
        {renderServiceCategoryDropdown()}
        {renderOffer()}
      </div>

      {/* Desktop actions */}
      <div className="md:ml-auto">
        <div className="flex flex-col justify-end items-center gap-4 md:flex-row md:gap-4">
          {showSendRequestButton && (
            <Button
              onClick={() => handleInviteAlliance()}
              disabled={isBusy || !selectedServiceCategoryId}
            >
              {isBusy
                ? t('quotes:createQuoteModal.sendingQuoteAllianceRequest')
                : t('quotes:createQuoteModal.sendQuoteAllianceRequest')}
            </Button>
          )}

          {(showCancelInviteButton || showRemoveSubmissionButton) && (
            <Button variant="primary" onClick={() => setShowRemoveConfirm(true)}>
              {showCancelInviteButton
                ? isBusy
                  ? t('quotes:createQuoteModal.cancellingQuoteAllianceRequest')
                  : t('quotes:createQuoteModal.cancelQuoteAllianceRequest')
                : isBusy
                  ? t('quotes:createQuoteModal.removingFromSubmission')
                  : t('quotes:createQuoteModal.removeFromSubmission')}
            </Button>
          )}

          {onClick && (
            <EyeButton
              onClick={onClick}
              ariaLabel={t('createQuoteModal.showAllianceButtonLabel')}
              outerClassName="hidden md:flex md:justify-self-end"
            />
          )}
        </div>
        {error && (
          <div className="flex text-[#F04438] text-[0.9375rem] font-ambit mt-4 md:flex-row-reverse md:items-center">
            {error}
          </div>
        )}
      </div>

      {showRemoveConfirm && (
        <FullBlockingModal innerClassName="md:max-w-xl">
          <h2 className="text-xl font-bold mb-4">
            {showCancelInviteButton
              ? t('createQuoteModal.cancelAllianceConfirmTitle')
              : t('createQuoteModal.removeAllianceConfirmTitle')}
          </h2>
          <p className="mb-4">
            {showCancelInviteButton
              ? t('createQuoteModal.cancelAllianceConfirmText', { name: freelancer.first_name })
              : t('createQuoteModal.removeAllianceConfirmText', { name: freelancer.first_name })}
          </p>
          <div className="flex gap-4 justify-end mt-4">
            <Button
              variant="transparent-with-border"
              onClick={() => {
                setError(null);
                setShowRemoveConfirm(false);
              }}
            >
              {t('common:form_actions.cancel', { ns: 'common', defaultValue: 'Annuler' })}
            </Button>
            <Button variant="primary" onClick={handleRetireAlliance}>
              {t('common:form_actions.confirm', { ns: 'common', defaultValue: 'Confirmer' })}
            </Button>
          </div>
        </FullBlockingModal>
      )}

      {showInviteModal && (
        <QuoteAllianceInviteModal
          freelancer={{
            id: userAlliance?.user.id ?? '',
            first_name: userAlliance?.user.first_name ?? '',
            avatar_url: userAlliance?.user.avatar_url ?? '',
          }}
          quoteId={quote?.id ?? ''}
          job_title={quote?.job.title ?? ''}
          serviceCategoryId={selectedServiceCategoryId}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
};

export default QuoteFreelancerCard;
